import React from "react";
import PropTypes from 'prop-types';
import ActionDialog from "../Shared/ActionDialog";
import { impersonateVendor } from "../../services/authServices";
import api from "../../proxy/config";
import { useUI } from "../Context/UIContext";

/**
 * Componente per la gestione del dialogo di impersonificazione vendor.
 * Incapsula la logica di recupero del token e l'apertura del portale tramite postMessage.
 */
const ImpersonateDialog = ({
  open,
  onClose,
  vendor,
  performAsyncAction
}) => {
  const { showError } = useUI();

  const handleConfirm = () => {
    const FE_VENDOR_URL = api.feVendorImpersonateLogin;

    if (!vendor) return;

    // Apriamo subito la tab vuota (context del click) per evitare blocchi popup
    // mentre attendiamo la chiamata asincrona per il token
    const win = window.open('about:blank', '_blank');

    if (!win) {
      showError("Il popup è stato bloccato dal browser. Abilita i popup per questo sito.");
      return;
    }

    // Feedback di caricamento iniziale nella tab attiva
    win.document.body.style.fontFamily = "sans-serif";
    win.document.body.style.display = "flex";
    win.document.body.style.alignItems = "center";
    win.document.body.style.justifyContent = "center";
    win.document.body.style.height = "100vh";
    win.document.body.style.backgroundColor = "#f8fbff";
    win.document.body.style.margin = "0";
    win.document.body.innerHTML = "<h3>Connessione al portale Vendor in corso...</h3>";

    performAsyncAction(
      async () => {
        const response = await impersonateVendor(vendor.vendorListId);
        const token = response?.token || response?.impersonated_token;

        if (token) {
          const t = typeof token === "string" ? token.trim() : "";
          if (!t) throw new Error("Token vuoto");

          // Calcoliamo l'origine dinamicamente dall'URL del vendor
          const childOrigin = new URL(FE_VENDOR_URL).origin;

          // Re-indirizziamo la tab all'URL di login
          win.location.href = FE_VENDOR_URL;

          let sent = false;
          const sendToken = () => {
            if (sent) return;
            sent = true;
            win.postMessage({ token: t }, childOrigin);
          };

          const onReady = (evt) => {
            // Verifichiamo che la sorgente sia la finestra aperta e l'origine corretta
            if (evt?.source !== win) return;

            // Handshake come da protocollo wcp (es: wcp:vendor:impersonate-ready)
            if (evt?.data?.type === "wcp:vendor:impersonate-ready") {
              sendToken();
              window.removeEventListener("message", onReady);
            }
          };

          window.addEventListener("message", onReady);

          // Fallback: invio forzato dopo 1.2 secondi se l'handshake non arriva
          setTimeout(() => {
            sendToken();
            window.removeEventListener("message", onReady);
          }, 1200);

        } else {
          win.close();
          throw new Error("Token impersonato mancante nella risposta del server.");
        }
      },
      () => onClose(),
      true // skipFetch: true (non serve ricaricare la lista admin dopo l'impersonificazione)
    );
  };

  return (
    <ActionDialog
      open={open}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Impersonifica Ecommerce"
      actionType="Impersonificazione"
      vendorName={vendor?.businessName}
      confirmText="Avvia Impersonificazione"
      confirmColor="#2196f3"
      showReasonField={false}
      showEmailCheckbox={false}
    />
  );
};

ImpersonateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vendor: PropTypes.object,
  performAsyncAction: PropTypes.func.isRequired
};

export default ImpersonateDialog;
 